import { useCallback, useEffect, useReducer } from 'react'
import * as cognito from '../cognito'

import { LOGIN_FAILURE, LOGIN_NEW_PASSWORD_REQUIRED } from '../cognito/statuses'

const SESSION_TYPES = {
  LOGIN_BEGIN: 'begin:login',
  LOGIN_ERROR: 'error:login',
  LOGIN_ERROR_NEW_PASSWORD_REQUIRED: 'error:login:new-password-required',
  LOGIN_SUCCESS: 'success:login',
  LOGOUT_BEGIN: 'begin:logout',
  LOGOUT_SUCCESS: 'success:logout',
  RESTORE_SESSION_BEGIN: 'begin:restore-session',
  RESTORE_SESSION_ERROR: 'error:restore-session',
  RESTORE_SESSION_SUCCESS: 'success:restore-session',
  REFRESH_TOKENS_SUCCESS: 'success:refresh-tokens',
}
const action = (type, payload = undefined) => ({ type, payload })

const DEFAULT_SESSION_STATE = {
  refreshToken: null,
  idToken: null,
  accessToken: null,
  session: null,
  user: null,
  isLoggingIn: false,
  isLoggingOut: false,
  isLoggedIn: false,
  errors: null,
}
const sessionReducer = (state, action) => {
  const { type, payload } = action
  switch (type) {
    case SESSION_TYPES.LOGIN_BEGIN: {
      return {
        ...state,
        isLoggingIn: true,
        errors: null,
      }
    }
    case SESSION_TYPES.LOGIN_ERROR_NEW_PASSWORD_REQUIRED: {
      return {
        ...state,
        isLoggingIn: false,
        errors: {
          errors: [{ key: 'login:error:form:user-requires-new-password' }],
        },
      }
    }
    case SESSION_TYPES.LOGIN_ERROR: {
      return {
        ...state,
        isLoggingIn: false,
        errors: {
          errors: [{ key: 'login:error:form:user-not-found' }],
        },
      }
    }
    case SESSION_TYPES.LOGIN_SUCCESS: {
      const { newSession } = payload
      return {
        ...state,
        session: newSession,
        idToken: newSession.getIdToken(),
        accessToken: newSession.getAccessToken(),
        refreshToken: newSession.getRefreshToken(),
        isLoggedIn: true,
        isLoggingIn: false,
        user: {
          cognitoId: cognito.getUserAttributeFromSession(
            newSession,
            'cognito:username',
          ),
          userName: cognito.getUserAttributeFromSession(newSession, 'name'),
          email: cognito.getUserAttributeFromSession(newSession, 'email'),
          loginDate: new Date().toISOString(),
        },
      }
    }
    case SESSION_TYPES.LOGOUT_BEGIN: {
      return {
        ...state,
        isLoggingOut: true,
      }
    }
    case SESSION_TYPES.LOGOUT_SUCCESS: {
      return {
        ...DEFAULT_SESSION_STATE,
      }
    }
    case SESSION_TYPES.RESTORE_SESSION_BEGIN: {
      return {
        ...state,
        isLoggedIn: false,
      }
    }
    case SESSION_TYPES.RESTORE_SESSION_SUCCESS: {
      const { sessionData } = payload
      return {
        ...state,
        isLoggedIn: true,
        idToken: sessionData.getIdToken(),
        accessToken: sessionData.getAccessToken(),
        refreshToken: sessionData.getRefreshToken(),
        user: {
          cognitoId: cognito.getUserAttributeFromSession(
            sessionData,
            'cognito:username',
          ),
          userName: cognito.getUserAttributeFromSession(sessionData, 'name'),
          email: cognito.getUserAttributeFromSession(sessionData, 'email'),
          loginDate: new Date().toISOString(),
        },
      }
    }
    case SESSION_TYPES.REFRESH_TOKENS_SUCCESS: {
      const { newSession } = payload
      return {
        ...state,
        accessToken: newSession.getAccessToken(),
        refreshToken: newSession.getRefreshToken(),
        idToken: newSession.getIdToken(),
      }
    }
    default: {
      console.error(
        `Unknown action type ${type}. Doing nothing, check for typos.`,
      )
      return state
    }
  }
}

const useSession = ({ logoutCallback }) => {
  const [session, dispatch] = useReducer(sessionReducer, DEFAULT_SESSION_STATE)
  const login = useCallback(async (username, password) => {
    await logout()
    dispatch(action(SESSION_TYPES.LOGIN_BEGIN))
    return cognito
      .authenticateUser(username, password)
      .then(value => {
        const accessToken = value.data.session.getAccessToken()

        localStorage.setItem(
          process.env.REACT_APP_JWT_TOKEN_KEY,
          accessToken?.jwtToken,
        )

        localStorage.setItem(
          process.env.REACT_APP_ID_TOKEN_KEY,
          value.data.session.getIdToken().jwtToken,
        )

        return value
      })
      .then(({ data: { session: newSession } }) => {
        dispatch(
          action(SESSION_TYPES.LOGIN_SUCCESS, {
            newSession,
          }),
        )

        const loginSuccess = true
        return loginSuccess
      })
      .catch(reason => {
        const urlParams = new URLSearchParams(window.location.search)
        const redirect = urlParams.get('oldweb') === 'true' ? false : true
        if (reason.status === LOGIN_FAILURE && redirect) {
          submitForm(username, password)
        } else if (reason.status === LOGIN_FAILURE && !redirect) {
          dispatch(action(SESSION_TYPES.LOGIN_ERROR))
        } else if (reason.status === LOGIN_NEW_PASSWORD_REQUIRED) {
          const {
            status,
            data: { user, userAttributes },
          } = reason

          console.error('Unimplemented new password required login flow', {
            user,
            userAttributes,
            status,
          })

          dispatch(action(SESSION_TYPES.LOGIN_ERROR_NEW_PASSWORD_REQUIRED))
        } else {
          console.error('Unhandled login error', reason)

          dispatch(action(SESSION_TYPES.LOGIN_ERROR))
        }

        const loginSuccess = false
        return loginSuccess
      })
  }, [])

  const lowerPasswordLogin = (username, password) => {
    const urlParams = new URLSearchParams(window.location.search)
    const redirect = urlParams.get('oldweb') === 'true' ? false : true

    if (redirect) {
      submitForm(username, password)
      return false
    } else {
      dispatch(action(SESSION_TYPES.LOGIN_ERROR))
      return true
    }
  }

  const logout = useCallback(async () => {
    const user = await cognito.getUserFromPool().catch(error => {})
    if (user === undefined) {
      return
    }

    dispatch(action(SESSION_TYPES.LOGOUT_BEGIN))

    return cognito
      .signOut(user)
      .then(value => {
        localStorage.removeItem(process.env.REACT_APP_JWT_TOKEN_KEY)

        return value
      })
      .then(() => {
        dispatch(action(SESSION_TYPES.LOGOUT_SUCCESS))

        if (typeof logoutCallback === 'function') {
          logoutCallback()
        }
      })
  }, [logoutCallback])

  const updateTokens = useCallback(async () => {
    const user = await cognito.getUserFromPool()

    return cognito
      .refreshSession(user, session.refreshToken)
      .then(({ data: { newSession } }) => {
        dispatch(action(SESSION_TYPES.REFRESH_TOKENS_SUCCESS, { newSession }))

        return {
          accessToken: newSession.getAccessToken(),
          refreshToken: newSession.getRefreshToken(),
          idToken: newSession.getIdToken(),
        }
      })
  }, [session.refreshToken])

  const changePassword = useCallback(async (oldPassword, newPassword) => {
    const user = await cognito.getUserFromPool()

    return cognito.changePassword(user, oldPassword, newPassword)
  }, [])

  function submitForm(username, password) {
    let form = document.createElement('form')
    form.method = 'POST'
    form.action = process.env.REACT_APP_REDIRECT_URL

    let inputUsername = document.createElement('input')
    inputUsername.type = 'hidden'
    inputUsername.name = 'txtcodi'
    inputUsername.value = username

    let inputPassword = document.createElement('input')
    inputPassword.type = 'hidden'
    inputPassword.name = 'txtpass'
    inputPassword.value = password

    form.appendChild(inputUsername)
    form.appendChild(inputPassword)

    document.body.appendChild(form)
    form.submit()
  }

  useEffect(() => {
    cognito
      .getUserFromPool()
      .then(poolUser => {
        dispatch(action(SESSION_TYPES.RESTORE_SESSION_BEGIN))

        return cognito
          .loadSessionIntoPoolUser(poolUser)
          .then(sessionData => {
            dispatch(
              action(SESSION_TYPES.RESTORE_SESSION_SUCCESS, { sessionData }),
            )
          })
          .catch(error => {
            console.error('Error while checking session:', error)
            dispatch(action(SESSION_TYPES.RESTORE_SESSION_ERROR))
          })
      })
      .catch(() => {
        // NOTE Do nothing; there's no session persisted
      })
  }, [])

  return {
    session,
    login,
    logout,
    updateTokens,
    changePassword,
    lowerPasswordLogin,
  }
}

export { useSession }
